import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO } from "../lib/site";

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base grain flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(212,175,55,0.10),transparent_60%)]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO} alt="AKALIBE" className="h-14 w-auto" />
          <p className="text-xs uppercase tracking-[0.3em] text-gold mt-5">Espace artiste · privé</p>
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="backdrop-blur-2xl bg-white/[0.02] border border-white/[0.08] p-8 space-y-5 shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
        >
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] block mb-2">Email</label>
            <div className="flex items-center gap-3 bg-black/40 border border-white/[0.1] px-4 focus-within:border-gold/50 transition-colors">
              <Mail className="w-4 h-4 text-gold shrink-0" />
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@akalibe.com"
                className="flex-1 bg-transparent py-3.5 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] block mb-2">Mot de passe</label>
            <div className="flex items-center gap-3 bg-black/40 border border-white/[0.1] px-4 focus-within:border-gold/50 transition-colors">
              <Lock className="w-4 h-4 text-gold shrink-0" />
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="flex-1 bg-transparent py-3.5 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none"
              />
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-3 py-2">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-gold text-black font-medium py-4 hover:bg-gold-light transition-colors disabled:opacity-50 text-sm tracking-wide"
          >
            {loading ? "Connexion…" : "Entrer dans mon univers"}
          </button>
        </form>

        <a href="/" className="block text-center text-xs text-[#8A8A8A] hover:text-gold transition-colors mt-6">
          ← Retour au site
        </a>
      </motion.div>
    </div>
  );
}
